import { GridItem, Grid } from "@chakra-ui/react";
import { withoutNullable } from "@syla/shared/types/helpers/withoutNullable";
import {
  LedgerType,
  isReceiveType,
} from "@syla/shared/types/models/LedgerBase";
import { uniq, uniqueId } from "lodash";
import React, { Fragment, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { getAccountLedgerTypes } from "../../../../../../api/transactions/getAccountLedgerTypes";
import { getOptionForLedgerType } from "../../../../../../helper/transaction/getLedgerType";
import { useCurrentAccountStore } from "../../../../../../store/currentAccountStore";
import {
  getAccountTransactionsCacheKey,
  useTransactionsStore,
} from "../../../../../../store/transactionsStore";
import { OrderFilterSetting } from "../../../../../../types/order/orderQuery";
import { SingleSelector } from "../../../../../selector/SingleSelector";
import { setOperation, SwitchLedgerOperationEntry } from "../common";
import { ArrowGridItem } from "../components/ArrowGridItem";
import { DeleteButtonGridItem } from "../components/DeleteButtonGridItem";

export function LedgerBulkEdit({
  filters,
  operations,
  selectedIds,
  setOperations,
}: {
  operations: SwitchLedgerOperationEntry[];
  setOperations: React.Dispatch<
    React.SetStateAction<SwitchLedgerOperationEntry[]>
  >;
  selectedIds: string[];
  filters: OrderFilterSetting;
}) {
  const groups = useTransactionsStore((s) => s.groups);

  // all unique ledger types that are in transactions that have been
  // specifically selected by the user.
  // if the user has used 'select all pages', then this will be undefined.
  const selectedLedgerTypes: LedgerType[] | undefined = useMemo(() => {
    if (!groups || selectedIds.length === 0) {
      return undefined;
    }

    return uniq(
      withoutNullable(
        selectedIds.flatMap((gid) =>
          groups[gid].data.transactions.flatMap((transaction) =>
            transaction.ledgers.flatMap((ledger) => ledger.type),
          ),
        ),
      ),
    );
  }, [groups, selectedIds]);

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const { data: accountLedgerTypes, isLoading: accountLedgerTypesLoading } =
    useQuery(
      [...getAccountTransactionsCacheKey(accountId), "account-ledger-types"],
      () => getAccountLedgerTypes(accountId),
      {
        enabled: !selectedLedgerTypes,
      },
    );

  // Ledger types that have been selected by the user and are also selected
  // as a filter. This is a strong indication that the user is interested
  // in these ledgers.
  const filteredAndSelectedLedgerTypes = useMemo(
    () =>
      (selectedLedgerTypes ?? []).filter((ledgerType) =>
        filters.ledgerType.includes(ledgerType),
      ),
    [selectedLedgerTypes, filters.ledgerType],
  );

  // prepopulate operations based on selected types or filters
  useEffect(() => {
    // if there is only a single ledger type selected, then we can always
    // pre-populate the selection for convenience.
    if (selectedLedgerTypes !== undefined && selectedLedgerTypes.length === 1) {
      setOperations(
        selectedLedgerTypes.map((ledgerType) => ({
          id: uniqueId(),
          type: "ledger",
          from: ledgerType,
        })),
      );
    }

    // if the user has shown interest in a small number of ledger types, then
    // we can pre-populate the selection for convenience.
    else if (
      filteredAndSelectedLedgerTypes.length > 0 &&
      filteredAndSelectedLedgerTypes.length <= 3
    ) {
      setOperations(
        filteredAndSelectedLedgerTypes.map((ledgerType) => ({
          id: uniqueId(),
          type: "ledger",
          from: ledgerType,
        })),
      );
    }

    // if 'select all pages' is being used, in conjunction with a few filters,
    // then we can pre-populate the selection for convenience.
    else if (
      selectedLedgerTypes === undefined &&
      filters.ledgerType.length > 0 &&
      filters.ledgerType.length <= 3
    ) {
      setOperations(
        filters.ledgerType.map((ledgerType) => ({
          id: uniqueId(),
          type: "ledger",
          from: ledgerType,
        })),
      );
    }

    // otherwise, we don't pre-populate the selection, we just show an empty
    // from ledger selector.
    else {
      setOperations([{ id: uniqueId(), type: "ledger" }]);
    }
  }, [
    filteredAndSelectedLedgerTypes,
    selectedLedgerTypes,
    filters.ledgerType,
    setOperations,
  ]);

  return (
    <Grid
      w="full"
      templateColumns={{ base: "1fr", sm: "1fr auto 1fr auto" }}
      alignItems="center"
      justifyItems="flex-start"
      columnGap={4}
      rowGap={{ base: 2, sm: 4 }}
    >
      <Fragment>
        {operations.map((op) => (
          <Fragment key={op.id}>
            <LedgerOperation
              key={op.id}
              operation={op}
              fromLedgerTypes={
                // when we have knowledge of all the selected ledger types, we
                // can restrict the search space for the user to only those
                // which can actually be affected.
                selectedLedgerTypes ??
                // however, if 'select all pages' is being used, we don't have
                // the full list of selected ledger types, so we can only
                // safely restrict to the ledger types currently used in
                // this account.
                accountLedgerTypes ??
                []
              }
              fromLedgerTypesLoading={accountLedgerTypesLoading}
              setOperation={setOperation(op, setOperations)}
            />
            {operations.length > 1 && (
              <DeleteButtonGridItem
                operation={op}
                operations={operations}
                setOperations={setOperations}
              />
            )}
          </Fragment>
        ))}
      </Fragment>
    </Grid>
  );
}

function LedgerOperation({
  operation,
  setOperation,
  fromLedgerTypes,
  fromLedgerTypesLoading,
}: {
  operation: SwitchLedgerOperationEntry;
  setOperation: React.Dispatch<
    React.SetStateAction<SwitchLedgerOperationEntry>
  >;
  fromLedgerTypes: LedgerType[];
  fromLedgerTypesLoading?: boolean;
}): JSX.Element {
  const toLedgerTypes = useMemo(() => {
    const allOptions = Object.values(LedgerType);

    // can't convert to the same ledger type the user already has selected
    const filtered = allOptions.filter((type) => type !== operation.from);

    // Sort suggested to-ledger types so options matching the direction of the
    // selected from-ledger type appear first.
    const isInLedger =
      !operation.from ||
      isReceiveType({
        type: operation.from as LedgerType,
      });

    filtered.sort((a, b) => {
      const aMatches =
        isReceiveType({
          type: a,
        }) === isInLedger;

      const bMatches =
        isReceiveType({
          type: b,
        }) === isInLedger;

      if (aMatches === bMatches) {
        return 0;
      }

      return aMatches ? -1 : 1;
    });

    return filtered.map(getOptionForLedgerType);
  }, [operation.from]);

  return (
    <>
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          placeholder="Select Type"
          enableSearch
          value={operation.from}
          onChange={(value) => {
            setOperation((operation) => ({
              ...operation,
              from: value,
            }));
          }}
          options={fromLedgerTypes.map(getOptionForLedgerType)}
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
          isLoading={fromLedgerTypesLoading}
          autoSelectSingleItem
        />
      </GridItem>
      <ArrowGridItem />
      <GridItem display="contents">
        <SingleSelector
          moveSelectedToTop
          placeholder="Select Type"
          enableSearch
          value={operation.to}
          onChange={(selection) => {
            setOperation((operation) => ({
              ...operation,
              to: selection,
            }));
          }}
          options={toLedgerTypes}
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />
      </GridItem>
    </>
  );
}
